/**
 * Push notifications — admin (/naytive-push)
 *
 * Tabs:
 *   Campaigns  — one-off, scheduled and repeating pushes; filter by kind/status
 *   Automatic  — the 6 trigger templates (visa status, filing due, …): edit + turn on/off
 *   Audit      — who did what, when
 * Plus the composer (NaytivePushComposer) and a per-campaign report.
 */

import React, { useCallback, useEffect, useState } from 'react';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import { pushAPI, errMsg } from '../services/pushApi';
import PushComposer from './NaytivePushComposer';
import {
  EngineErrorBanner, KIND_LABELS, S, STATUSES, StatusPill, TRIGGERS, describeAudience, describeSchedule, fillVars, fmtDate,
} from './pushShared';

export default function NaytivePush() {
  const { user } = useAuth();
  const role = user?.role;
  const [tab, setTab] = useState('campaigns');
  const [view, setView] = useState(null); // { type: 'compose', item } | { type: 'report', id }
  const [toast, setToast] = useState(null);
  const [stats, setStats] = useState(null);
  const [notReady, setNotReady] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const flash = useCallback((m) => { setToast(m); setTimeout(() => setToast(null), 3200); }, []);

  useEffect(() => {
    pushAPI.deviceStats().then((r) => { setStats(r.data); setNotReady(''); })
      .catch((e) => { if (e?.response?.status === 503) setNotReady(errMsg(e)); });
  }, [reloadKey]);

  const openCompose = async (id) => {
    if (!id) { setView({ type: 'compose', item: null }); return; }
    try { const r = await pushAPI.get(id); setView({ type: 'compose', item: r.data.item }); }
    catch (e) { flash(errMsg(e, 'Could not load')); }
  };
  const back = () => { setView(null); setReloadKey((k) => k + 1); };

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="dashboard-main">
        <div style={{ padding: '24px 28px' }}>
          {view?.type === 'compose' ? (
            <PushComposer key={view.item?.id || 'new'} initial={view.item} role={role} flash={flash} onClose={back}
              onSaved={(item) => setView((v) => (v && v.type === 'compose' ? { ...v, item } : v))} />
          ) : view?.type === 'report' ? (
            <Report id={view.id} onBack={back} flash={flash} />
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <h1 style={{ fontSize: 22, fontWeight: 600, margin: 0, color: '#111827' }}>Push notifications</h1>
                  <div style={{ fontSize: 13, color: '#6B7280', marginTop: 3 }}>
                    Send notifications to members' iPhones. Every push also appears in the member's in-app Activity feed.
                    {stats && <> · <b style={{ color: '#374151' }}>{stats.members_with_app.toLocaleString()}</b> members have the app on <b style={{ color: '#374151' }}>{stats.active_devices.toLocaleString()}</b> devices</>}
                  </div>
                </div>
                {tab === 'campaigns' && <button style={S.btnPrimary} onClick={() => openCompose(null)}>New push</button>}
              </div>
              {notReady && <div style={{ ...S.banner, marginTop: 14 }}>{notReady}. The page will work once the push engine is deployed.</div>}
              <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #E5E7EB', margin: '18px 0 20px' }}>
                {[['campaigns', 'Campaigns'], ['automatic', 'Automatic'], ['audit', 'Audit log']].map(([k, l]) => (
                  <button key={k} onClick={() => setTab(k)}
                    style={{ padding: '9px 16px', border: 'none', background: 'none', fontSize: 13.5, fontWeight: 500,
                             color: tab === k ? '#111827' : '#6B7280', cursor: 'pointer',
                             borderBottom: tab === k ? '2px solid #2540D9' : '2px solid transparent', marginBottom: -1 }}>
                    {l}
                  </button>
                ))}
              </div>
              {tab === 'campaigns' && <CampaignsTab key={reloadKey} flash={flash} onEdit={openCompose} onReport={(id) => setView({ type: 'report', id })} />}
              {tab === 'automatic' && <AutomaticTab key={reloadKey} flash={flash} onEdit={openCompose} onReport={(id) => setView({ type: 'report', id })} />}
              {tab === 'audit' && <AuditTab key={reloadKey} />}
            </>
          )}
        </div>
      </main>
      {toast && <div style={S.toast}>{toast}</div>}
    </div>
  );
}

// ─────────────────────────────── Campaigns ───────────────────────────────
function CampaignsTab({ flash, onEdit, onReport }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = {};
      if (kind) params.kind = kind;
      if (status) params.status = status;
      const r = await pushAPI.list(params);
      setItems((r.data.items || []).filter((c) => c.kind !== 'triggered'));
    } catch (e) { setItems([]); setError(errMsg(e, 'Could not load campaigns')); }
    finally { setLoading(false); }
  }, [kind, status]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, okMsg, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    try { await fn(); flash(okMsg); await load(); } catch (e) { flash(errMsg(e)); }
  };

  return (
    <>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <select style={{ ...S.inp, width: 'auto' }} value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="">All types</option>
          {['instant', 'scheduled', 'recurring'].map((k) => <option key={k} value={k}>{KIND_LABELS[k]}</option>)}
        </select>
        <select style={{ ...S.inp, width: 'auto' }} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      {loading ? <div style={S.empty}>Loading…</div>
        : error ? <div style={S.empty}>{error}</div>
        : items.length === 0 ? <div style={S.empty}>No push campaigns{kind || status ? ' match these filters' : ' yet. Click “New push” to write one'}.</div>
        : (
          <div style={S.card}>
            <table style={S.table}><thead><tr style={S.thead}>
              <th style={S.th}>Campaign</th><th style={S.th}>When</th><th style={S.th}>Who</th>
              <th style={S.th}>Status</th><th style={S.th}>Results</th><th style={S.th}></th>
            </tr></thead><tbody>
              {items.map((c) => (
                <tr key={c.id} style={{ borderTop: '1px solid #F3F4F6' }}>
                  <td style={S.td}>
                    <div style={{ fontWeight: 600, color: '#111827', cursor: 'pointer' }} onClick={() => onEdit(c.id)}>{c.name}</div>
                    <div style={{ fontSize: 12, color: '#6B7280', maxWidth: 320, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.title}</div>
                    <div style={{ fontSize: 11.5, color: '#9CA3AF' }}>{KIND_LABELS[c.kind]} · {c.category}{c.created_by_name ? ` · by ${c.created_by_name}` : ''}</div>
                    {c.engine_error && <div style={{ fontSize: 11.5, color: '#B91C1C', fontWeight: 600, marginTop: 2 }}>Paused by the system: {c.engine_error.error || 'see report'}</div>}
                  </td>
                  <td style={S.td}>
                    <div>{describeSchedule(c)}</div>
                    {c.next_run_at && ['scheduled', 'active'].includes(c.status) && <div style={{ fontSize: 11.5, color: '#6B7280' }}>Next: {fmtDate(c.next_run_at)}</div>}
                    {c.kind === 'recurring' && c.runs_done > 0 && <div style={{ fontSize: 11.5, color: '#6B7280' }}>Sent {c.runs_done} time{c.runs_done === 1 ? '' : 's'}</div>}
                  </td>
                  <td style={S.td}>{describeAudience(c.audience_json)}</td>
                  <td style={S.td}><StatusPill status={c.status} /></td>
                  <td style={S.td}>
                    {c.sent + c.failed + c.held > 0
                      ? <span>{c.sent} delivered · {c.opened} opened{c.failed ? ` · ${c.failed} failed` : ''}{c.held ? ` · ${c.held} waiting` : ''}</span>
                      : <span style={{ color: '#9CA3AF' }}>—</span>}
                  </td>
                  <td style={{ ...S.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button style={S.mini} onClick={() => onEdit(c.id)}>{['draft', 'paused'].includes(c.status) ? 'Edit' : 'View'}</button>
                    {c.status !== 'draft' && <button style={{ ...S.mini, marginLeft: 6 }} onClick={() => onReport(c.id)}>Report</button>}
                    <button style={{ ...S.mini, marginLeft: 6 }} onClick={() => act(() => pushAPI.duplicate(c.id), 'Copied as a new draft')}>Duplicate</button>
                    {['scheduled', 'active', 'sending'].includes(c.status) && (
                      <button style={{ ...S.mini, marginLeft: 6 }} onClick={() => act(() => pushAPI.pause(c.id), 'Paused')}>Pause</button>
                    )}
                    {!['completed', 'cancelled', 'draft'].includes(c.status) && (
                      <button style={{ ...S.mini, marginLeft: 6, color: '#B91C1C' }} onClick={() => act(() => pushAPI.cancel(c.id), 'Cancelled', 'Cancel this campaign? Nothing more will be sent, including notifications waiting for quiet hours to end.')}>Cancel</button>
                    )}
                    {['draft', 'cancelled'].includes(c.status) && (
                      <button style={{ ...S.mini, marginLeft: 6, color: '#B91C1C' }} onClick={() => act(() => pushAPI.remove(c.id), 'Deleted', 'Delete this campaign?')}>Delete</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody></table>
          </div>
        )}
    </>
  );
}

// ─────────────────────────────── Automatic ───────────────────────────────
function AutomaticTab({ flash, onEdit, onReport }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { const r = await pushAPI.list({ kind: 'triggered' }); setItems(r.data.items || []); }
    catch (e) { setItems([]); setError(errMsg(e, 'Could not load templates')); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (c) => {
    setBusyId(c.id);
    try {
      if (c.status === 'active') { await pushAPI.pause(c.id); flash('Turned off'); }
      else { await pushAPI.approve(c.id); flash('Turned on'); }
      await load();
    } catch (e) { flash(errMsg(e)); }
    finally { setBusyId(null); }
  };

  if (loading) return <div style={S.empty}>Loading…</div>;
  if (error) return <div style={S.empty}>{error}</div>;
  if (!items.length) return <div style={S.empty}>No automatic templates yet. They appear here once the push engine is deployed.</div>;

  return (
    <>
      <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 14 }}>
        These go out on their own when something happens to a member. Edit the wording, then turn them on. Each one only goes to the member it is about.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 14 }}>
        {items.map((c) => {
          const t = TRIGGERS[c.trigger_key] || { label: c.trigger_key, when: '', vars: [] };
          const on = c.status === 'active';
          return (
            <div key={c.id} style={{ ...S.section, marginBottom: 0, display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14.5, fontWeight: 600, color: '#111827' }}>{t.label}</div>
                  <div style={{ fontSize: 12.5, color: '#6B7280', marginTop: 2 }}>{t.when}</div>
                </div>
                <button onClick={() => toggle(c)} disabled={busyId === c.id || c.status === 'cancelled'} title={on ? 'Turn off' : 'Turn on'}
                  style={{ width: 44, height: 24, borderRadius: 999, border: 'none', cursor: 'pointer', position: 'relative', background: on ? '#10B981' : '#D1D5DB', flexShrink: 0 }}>
                  <span style={{ position: 'absolute', top: 3, left: on ? 23 : 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left .15s' }} />
                </button>
              </div>
              <div style={{ margin: '12px 0', padding: '10px 12px', borderRadius: 12, background: '#F3F4F6', fontSize: 13 }}>
                <div style={{ fontWeight: 600, color: '#111827' }}>{fillVars(c.title)}</div>
                {c.subtitle && <div style={{ fontWeight: 500, color: '#111827' }}>{fillVars(c.subtitle)}</div>}
                <div style={{ color: '#374151' }}>{fillVars(c.body)}</div>
              </div>
              <div style={{ fontSize: 12, color: '#6B7280' }}>
                Variables: {t.vars.map((v) => <code key={v} style={{ background: '#EEF2FF', color: '#3730A3', padding: '1px 5px', borderRadius: 4, marginRight: 4 }}>{`{{${v}}}`}</code>)}
              </div>
              {c.trigger_key === 'filing.due' && (
                <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>Sends {(c.trigger_offset_json?.days_before || [7, 1]).join(' and ')} days before the due date, and when overdue.</div>
              )}
              <div style={{ display: 'flex', gap: 6, marginTop: 'auto', paddingTop: 12, alignItems: 'center' }}>
                <StatusPill status={c.status} />
                {c.engine_error && <span style={{ fontSize: 11.5, color: '#B91C1C', fontWeight: 600 }}>Paused by the system</span>}
                <span style={{ fontSize: 12, color: '#9CA3AF', flex: 1 }}>{c.sent ? `${c.sent} delivered · ${c.opened} opened` : ''}</span>
                <button style={S.mini} onClick={() => onReport(c.id)}>Report</button>
                <button style={S.mini} onClick={() => onEdit(c.id)}>Edit template</button>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

// ─────────────────────────────── Audit ───────────────────────────────
const AUDIT_LABELS = {
  create: 'Created', update: 'Edited', approve: 'Approved & started', send: 'Sent', test: 'Sent a test',
  pause: 'Paused', resume: 'Resumed', cancel: 'Cancelled', duplicate: 'Duplicated', delete: 'Deleted',
  upload_image: 'Uploaded an image', approve_failed: 'Start failed', resume_failed: 'Resume failed',
  engine_error: 'Paused by the system',
};
function AuditTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(null);
  useEffect(() => {
    pushAPI.audit({ limit: 300 }).then((r) => setItems(r.data.items || []))
      .catch((e) => setError(errMsg(e, 'Could not load the audit log'))).finally(() => setLoading(false));
  }, []);
  if (loading) return <div style={S.empty}>Loading…</div>;
  if (error) return <div style={S.empty}>{error}</div>;
  if (!items.length) return <div style={S.empty}>Nothing has happened yet.</div>;
  return (
    <div style={S.card}>
      <table style={S.table}><thead><tr style={S.thead}>
        <th style={S.th}>When</th><th style={S.th}>Who</th><th style={S.th}>What</th><th style={S.th}>Campaign</th><th style={S.th}>Details</th>
      </tr></thead><tbody>
        {items.map((a) => (
          <tr key={a.id} style={{ borderTop: '1px solid #F3F4F6' }}>
            <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{fmtDate(a.created_at)}</td>
            <td style={S.td}>{a.admin_name || a.admin_email || (a.admin_id ? `#${a.admin_id}` : 'System')}</td>
            <td style={{ ...S.td, ...(a.action === 'engine_error' ? { color: '#B91C1C', fontWeight: 600 } : {}) }}>{AUDIT_LABELS[a.action] || a.action}{a.action === 'engine_error' && a.detail_json?.error ? `: ${a.detail_json.error}` : ''}</td>
            <td style={S.td}>{a.campaign_name || (a.campaign_id ? `#${a.campaign_id}` : '—')}</td>
            <td style={S.td}>
              {a.detail_json ? (
                <button style={S.mini} onClick={() => setOpen(open === a.id ? null : a.id)}>{open === a.id ? 'Hide' : 'Show'}</button>
              ) : '—'}
              {open === a.id && <pre style={{ fontSize: 11.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: 420, margin: '6px 0 0', color: '#4B5563' }}>{JSON.stringify(a.detail_json, null, 2)}</pre>}
            </td>
          </tr>
        ))}
      </tbody></table>
    </div>
  );
}

// ─────────────────────────────── Report ───────────────────────────────
const SKIP_LABELS = {
  no_device: 'No app installed', pref_off: 'Turned this category off', cap: 'Daily limit reached',
  quiet_hold: 'Held for quiet hours', disabled: 'Device no longer valid', test: 'Test', cancelled: 'Campaign cancelled',
  stale: 'Too late to send (over 6 hours overdue)', local_time: 'Waiting for the member’s local time', snooze: 'Snoozed by the member',
  engine_error: 'Engine error',
};
function Report({ id, onBack, flash }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const load = useCallback(() => {
    pushAPI.report(id).then((r) => setData(r.data)).catch((e) => setError(errMsg(e, 'Could not load the report')));
  }, [id]);
  useEffect(() => { load(); }, [load]);
  if (error) return <><button style={S.btnGhost} onClick={onBack}>← Back</button><div style={{ ...S.empty, marginTop: 16 }}>{error}</div></>;
  if (!data) return <div style={S.empty}>Loading…</div>;
  const t = data.totals;
  const c = data.campaign;
  const tiles = [
    ['Sent', t.sent, 'Handed to Apple'],
    ['Delivered', t.delivered, 'Apple accepted it for the phone'],
    ['Waiting', t.held + t.queued, 'Held for quiet hours or local time'],
    ['Retrying', t.retrying, 'Failed for now, will try again'],
    ['Failed', t.failed, 'Apple rejected it'],
    ['Opened', t.opened, 'Member tapped it'],
    ['Button tapped', t.action_taken, 'Member used a button'],
    ['Open rate', `${t.open_rate}%`, 'Opened ÷ delivered'],
  ];
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <button style={S.btnGhost} onClick={onBack}>← Back</button>
        <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0, color: '#111827' }}>{c.name}</h2>
        <StatusPill status={c.status} />
        <button style={{ ...S.mini, marginLeft: 'auto' }} onClick={() => { load(); flash('Refreshed'); }}>Refresh</button>
      </div>
      <EngineErrorBanner err={c.engine_error} />
      <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 14 }}>
        “{c.title}” · {KIND_LABELS[c.kind]} · {describeSchedule(c)} · {c.kind === 'triggered' ? 'the member the event is about' : describeAudience(c.audience_json)}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10, marginBottom: 18 }}>
        {tiles.map(([l, v, h]) => (
          <div key={l} style={{ ...S.section, marginBottom: 0, padding: 14 }}>
            <div style={{ fontSize: 12, color: '#6B7280' }}>{l}</div>
            <div style={{ fontSize: 24, fontWeight: 600, color: '#111827', marginTop: 2 }}>{typeof v === 'number' ? v.toLocaleString() : v}</div>
            <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>{h}</div>
          </div>
        ))}
      </div>
      <div style={{ fontSize: 12.5, color: '#6B7280', marginBottom: 18 }}>
        {t.members.toLocaleString()} members targeted · {t.skipped.toLocaleString()} skipped
        {data.skipped_by_reason.length > 0 && <> ({data.skipped_by_reason.map((s) => `${SKIP_LABELS[s.reason] || s.reason}: ${s.count}`).join(', ')})</>}
        {data.actions.length > 0 && <> · Buttons: {data.actions.map((a) => `${a.action_id} ${a.count}`).join(', ')}</>}
        {data.tests.sent > 0 && <> · {data.tests.sent} test send{data.tests.sent === 1 ? '' : 's'} (not counted)</>}
      </div>

      <h3 style={{ fontSize: 14, fontWeight: 600, margin: '0 0 8px' }}>By send</h3>
      {data.runs.length === 0 ? <div style={S.empty}>Nothing sent yet.</div> : (
        <div style={{ ...S.card, marginBottom: 18 }}>
          <table style={S.table}><thead><tr style={S.thead}>
            <th style={S.th}>#</th><th style={S.th}>First sent</th><th style={S.th}>Members</th><th style={S.th}>Delivered</th>
            <th style={S.th}>Waiting</th><th style={S.th}>Failed</th><th style={S.th}>Skipped</th><th style={S.th}>Opened</th><th style={S.th}>Open rate</th>
          </tr></thead><tbody>
            {data.runs.map((r) => (
              <tr key={r.run_no} style={{ borderTop: '1px solid #F3F4F6' }}>
                <td style={S.td}>{r.run_no}</td><td style={S.td}>{fmtDate(r.first_sent_at)}</td><td style={S.td}>{r.members}</td>
                <td style={S.td}>{r.delivered}</td><td style={S.td}>{r.held + r.queued}</td><td style={S.td}>{r.failed}</td>
                <td style={S.td}>{r.skipped}</td><td style={S.td}>{r.opened}</td><td style={S.td}>{r.open_rate}%</td>
              </tr>
            ))}
          </tbody></table>
        </div>
      )}

      {data.engine_errors?.length > 0 && (
        <>
          <h3 style={{ fontSize: 14, fontWeight: 600, margin: '0 0 8px', color: '#B91C1C' }}>System pauses</h3>
          <div style={{ ...S.card, marginBottom: 18 }}>
            <table style={S.table}><tbody>
              {data.engine_errors.map((e) => (
                <tr key={e.id} style={{ borderTop: '1px solid #F3F4F6' }}>
                  <td style={{ ...S.td, whiteSpace: 'nowrap', width: 170 }}>{fmtDate(e.at)}</td>
                  <td style={{ ...S.td, color: '#B91C1C' }}>{e.error || '—'}{e.run_no ? ` (send #${e.run_no})` : ''}</td>
                </tr>
              ))}
            </tbody></table>
          </div>
        </>
      )}
      <h3 style={{ fontSize: 14, fontWeight: 600, margin: '0 0 8px' }}>Recent failures and retries</h3>
      {data.recent_failures.length === 0 ? <div style={S.empty}>No failures.</div> : (
        <div style={S.card}>
          <table style={S.table}><thead><tr style={S.thead}>
            <th style={S.th}>When</th><th style={S.th}>Member</th><th style={S.th}>Send #</th><th style={S.th}>State</th><th style={S.th}>Error</th>
          </tr></thead><tbody>
            {data.recent_failures.map((f) => (
              <tr key={f.id} style={{ borderTop: '1px solid #F3F4F6' }}>
                <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{fmtDate(f.sent_at || f.created_at)}</td>
                <td style={S.td}>{f.email || `#${f.user_id}`}</td><td style={S.td}>{f.run_no}</td>
                <td style={S.td}>{f.status === 'held'
                  ? <span style={{ ...S.pill, background: '#FEF3C7', color: '#92400E' }}>Retrying (try {Math.min((Number(f.attempts) || 0) + 1, 4)} of 4)</span>
                  : <span style={{ ...S.pill, background: '#FEE2E2', color: '#991B1B' }}>failed</span>}
                  {f.status === 'held' && f.hold_until && <div style={{ fontSize: 11, color: '#9CA3AF' }}>next {fmtDate(f.hold_until)}</div>}</td>
                <td style={{ ...S.td, color: '#B91C1C' }}>{f.error || '—'}</td>
              </tr>
            ))}
          </tbody></table>
        </div>
      )}
    </div>
  );
}
